import React, { useEffect, useRef } from "react";
import "./otp.css";

const DIGIT_COUNT = 6;

/**
 * Shared OTP form visual used by:
 *  - the unified-login OTP verification page
 *  - the corporate signup setup page
 *
 * Renders the title/subtitle, the six OTP input boxes (with paste / arrow-key
 * navigation), the resend row (timer or button), and the submit button. The
 * caller owns submit and resend logic — this component is pure UI plus value
 * management for the digit array.
 *
 * @param {{
 *   title: string,
 *   subtitle?: React.ReactNode,
 *   value: string[],                       // length-6 array of single chars
 *   onValueChange: (next: string[]) => void,
 *   onSubmit: (e: React.FormEvent) => void,
 *   submitLabel?: string,
 *   submittingLabel?: string,
 *   isSubmitting?: boolean,
 *   error?: boolean,                       // tints inputs red when true
 *   resend?: {
 *     isTimerActive: boolean,
 *     timer: number,
 *     onResend: () => void,
 *     isResending?: boolean,
 *     idleText?: string,                   // e.g. "Didn't get code?"
 *     timerLabel?: (seconds:number)=>string
 *   },
 *   accentColor?: string,                  // overrides --auth-otp-accent
 *   children?: React.ReactNode,            // extra content (e.g. error banner)
 * }} props
 */
const AuthOtpForm = ({
  title,
  subtitle,
  value,
  onValueChange,
  onSubmit,
  submitLabel = "Verify",
  submittingLabel = "Verifying...",
  isSubmitting = false,
  error = false,
  resend,
  accentColor,
  children,
}) => {
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const setDigitAt = (index, digit) => {
    const next = [...value];
    next[index] = digit || "";
    onValueChange(next);
  };

  const handleChange = (index, raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (!digits) {
      setDigitAt(index, "");
      return;
    }
    if (digits.length === 1) {
      setDigitAt(index, digits);
      if (index < DIGIT_COUNT - 1) refs.current[index + 1]?.focus();
      return;
    }
    // Multi-char input (typically auto-fill or fast typing) — distribute.
    const next = [...value];
    let i = index;
    for (const d of digits) {
      if (i >= DIGIT_COUNT) break;
      next[i] = d;
      i++;
    }
    onValueChange(next);
    refs.current[Math.min(i, DIGIT_COUNT - 1)]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...value];
      if (next[index]) {
        next[index] = "";
        onValueChange(next);
      } else if (index > 0) {
        next[index - 1] = "";
        onValueChange(next);
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < DIGIT_COUNT - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    const next = Array(DIGIT_COUNT).fill("");
    for (let i = 0; i < Math.min(pasted.length, DIGIT_COUNT); i++) {
      next[i] = pasted[i];
    }
    onValueChange(next);
    refs.current[Math.min(pasted.length, DIGIT_COUNT - 1)]?.focus();
  };

  const filledCount = value.filter(Boolean).length;
  const isComplete = filledCount === DIGIT_COUNT;

  const wrapperStyle = accentColor
    ? { "--auth-otp-accent": accentColor }
    : undefined;

  return (
    <form onSubmit={onSubmit} noValidate style={wrapperStyle}>
      <h2 className="auth-otp-title">{title}</h2>
      {subtitle && <p className="auth-otp-subtitle">{subtitle}</p>}

      <div className="auth-otp-container">
        {Array.from({ length: DIGIT_COUNT }, (_, i) => i).map((i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            id={`auth-otp-input-${i}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={i === 0 ? DIGIT_COUNT : 1}
            value={value[i] || ""}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            disabled={isSubmitting}
            aria-label={`Digit ${i + 1}`}
            {...(i === 0
              ? { autoComplete: "one-time-code", name: "one-time-code" }
              : {})}
            className={`auth-otp-input ${error ? "has-error" : ""}`}
          />
        ))}
      </div>

      {resend && (
        <div className="auth-otp-resend">
          <span className="auth-otp-resend-text">
            {resend.idleText || "Didn't get code?"}
          </span>
          {resend.isTimerActive ? (
            <span className="auth-otp-resend-timer">
              {resend.timerLabel
                ? resend.timerLabel(resend.timer)
                : `Resend code in ${resend.timer}s`}
            </span>
          ) : (
            <button
              type="button"
              className="auth-otp-resend-button"
              onClick={resend.onResend}
              disabled={resend.isResending || isSubmitting}
            >
              {resend.isResending ? "Sending..." : "Resend code"}
            </button>
          )}
        </div>
      )}

      {children}

      <button
        type="submit"
        className={`auth-otp-submit ${
          isComplete && !isSubmitting ? "is-enabled" : ""
        }`}
        disabled={!isComplete || isSubmitting}
      >
        {isSubmitting ? submittingLabel : submitLabel}
      </button>
    </form>
  );
};

export default AuthOtpForm;
